import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Dumbbell, Heart, MapPin, Shield, Swords,
  TrendingUp, Sparkles,
} from 'lucide-react';
import { heroImages } from '../mock';
import BreathingFlameMark from '../components/BreathingFlameMark';

/**
 * Story — the "why Enferno exists" page. Hosts the mission, multi-discipline
 * positioning, feature grid, founder statement, and Ironbridge/IronLimb
 * partnership context. Moved here from Home.jsx, which is now a
 * minimal entry screen for the app.
 *
 * Linked from: footer "About / Story", Home's secondary CTA, Profile help
 * menu. Never auto-shown.
 */

const STATS = [
  { v: '5', l: 'Pillars: Strength · Combatives · Recovery · Nutrition · Rehab' },
  { v: '300+', l: 'Unique movements · honest count, no fluff' },
  { v: 'AI', l: 'Personalized to you' },
  { v: '∞', l: 'Variations per athlete' },
];

const FEATURES = [
  { icon: Heart, title: 'Train with purpose', body: 'A portion of every Enferno subscription funds IronLimb Foundation grants for amputee athletes\u2019 adaptive equipment. Your work fuels theirs.' },
  { icon: Dumbbell, title: 'AI-tuned strength programs', body: 'Push/Pull/Legs/Core, Upper/Lower, Bro Split, Full Body, Calisthenics — anchored to your start date, shaped by your equipment, refined by your data.' },
  { icon: Shield, title: 'Pain-aware programming', body: 'Daily check-in adjusts intensity. Injured shoulder? Enferno substitutes the exercise — never just deletes the day.' },
  { icon: MapPin, title: 'GPS-enabled endurance', body: 'Walks, runs, rides, swims, climbs, rows — full guidance, indoor and outdoor surfaces, Easy/Steady/Sport intensity scaling.' },
  { icon: Swords, title: 'Martial arts tracking', body: 'BJJ, Muay Thai, Judo, Boxing. Track rank progressions, log sessions, upload sparring footage.' },
  { icon: TrendingUp, title: 'Built for every body', body: 'Beginner to advanced. Equipment-aware. Time-tiered (30/45/60 min). Limitation-respecting. No assumptions.' },
];

const MISSION = [
  { eyebrow: 'Why we exist', title: 'One app for the whole athlete.', body: 'Most apps are built for one sport, one age, one body. Enferno was built for the rest of us — the multi-discipline, mid-life, pain-aware, limitation-respecting athletes who refuse to quit.' },
  { eyebrow: 'Who we partner with', title: 'IronLimb Foundation.', body: 'A portion of every subscription funds the IronLimb Foundation\'s mission as well. You train. They help. The heat benefits everyone who steps into it.' },
  { eyebrow: "What's next", title: 'The Enferno is heating up.', body: 'Cloud sync, AI coach chat, dynamic workout generation, device integrations, and the Impact dashboard are all on the roadmap. See where this is headed.', linkTo: '/roadmap', linkLabel: 'See the roadmap' },
];

const Story = () => {
  return (
    <div className="bg-transparent text-enf-text-primary">
      {/* HERO */}
      <section className="relative pt-32 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImages.strength} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
        </div>
        <div className="relative max-w-4xl mx-auto">
          <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-3">Our story</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-wide leading-[0.95] mb-6">
            Built for the rest of us.
          </h1>
          <p className="text-enf-text-secondary text-lg leading-relaxed max-w-2xl">
            Most apps in this category are built for one sport, one age, one body. Enferno was built for the multi-discipline, mid-life, pain-aware, limitation-respecting athletes who refuse to quit.
          </p>
        </div>
      </section>

      {/* HONEST STATS */}
      <section className="border-y border-enf-surface-border bg-enf-surface-deep" data-testid="story-stats-bar">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.l}>
              <div className="font-display text-5xl md:text-6xl tracking-wide leading-none">{s.v}</div>
              <div className="text-xs uppercase tracking-widest text-enf-role-info mt-3 font-semibold">{s.l}</div>
            </div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-6 -mt-2 text-xs text-enf-text-muted">Honest counts from today's build. Numbers grow each release.</div>
      </section>

      {/* MULTI-DISCIPLINE */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-10">
            <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-3">One athlete. Every discipline.</div>
            <h2 className="font-display text-5xl md:text-7xl tracking-wide leading-none mb-5">The athlete is made under heat.</h2>
            <p className="text-enf-text-secondary text-lg leading-relaxed">
              Lift. Run. Ride. Roll. Strike. Recover. Enferno adapts to whatever you train for — and works around whatever you're working through.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {['Strength', 'Cycling', 'Running', 'Martial Arts', 'Swim', 'Yoga', 'Mobility', 'Hiking'].map((d) => (
              <span key={d} className="px-3 py-1.5 rounded-full bg-enf-surface-deep border border-enf-surface-border text-sm text-enf-text-secondary">{d}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="py-24 px-6 bg-enf-surface-deep border-y border-enf-surface-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-3">Built for that life</div>
              <h2 className="font-display text-5xl md:text-6xl tracking-wide leading-none">Built by athletes who wanted one app &mdash; not four.</h2>
            </div>
            <p className="text-enf-text-secondary max-w-md">
              Enferno exists because you are so much more than a single-sport athlete. You lift on Monday, run on Tuesday, combat train on Wednesday, ride on Saturday, and recover on Sunday. Tweaked shoulder, bum knee, career, family &mdash; <span className="text-enf-accent-fg font-semibold">BRING IT</span>. Enferno supports you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-enf-surface-mid rounded-xl overflow-hidden border border-enf-surface-border" data-testid="feature-grid">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-enf-surface-mid p-8 hover:bg-enf-surface-deep transition-colors group">
                  <div className="w-12 h-12 rounded-md bg-enf-surface-mid group-hover:bg-enf-accent flex items-center justify-center mb-5 transition-colors">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="font-display text-2xl tracking-wide mb-2">{f.title}</div>
                  <p className="text-enf-text-secondary text-sm leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MISSION TRIPTYCH */}
      <section className="py-24 px-6 bg-enf-surface-deep border-y border-enf-surface-border">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-3">Mission</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-wide leading-none">Built on purpose.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5" data-testid="mission-triptych">
            {MISSION.map((m) => (
              <div key={m.title} className="bg-enf-surface-mid border border-enf-surface-border rounded-xl p-7 hover:border-enf-surface-border transition-colors flex flex-col">
                <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-3">{m.eyebrow}</div>
                <div className="font-display text-3xl tracking-wide leading-none mb-4">{m.title}</div>
                <p className="text-enf-text-secondary text-sm leading-relaxed flex-1">{m.body}</p>
                {m.linkTo && (
                  <Link to={m.linkTo} className="mt-5 inline-flex items-center gap-2 text-enf-role-info hover:text-enf-text-primary text-sm font-semibold transition-colors">
                    {m.linkLabel} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER STATEMENT */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="founder-statement">
        <div className="absolute inset-0">
          <img src={heroImages.strength} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black" />
        </div>
        <div className="relative max-w-4xl mx-auto">
          <div className="flex items-start gap-5 mb-8">
            <BreathingFlameMark size={88} intensity="lively" className="flex-shrink-0 hidden sm:flex" />
            <div>
              <div className="text-enf-accent-fg text-xs uppercase tracking-[0.3em] font-bold mb-3">Why Enferno exists</div>
              <h2 className="font-display text-4xl md:text-6xl tracking-wide leading-[0.95]">More than fitness.</h2>
            </div>
          </div>
          <div className="space-y-5 text-enf-text-secondary text-base md:text-lg leading-relaxed max-w-3xl">
            <p>
              Five pillars. One app. <span className="text-enf-text-primary font-semibold">Strength</span>,
              {' '}<span className="text-enf-text-primary font-semibold">Endurance</span>,
              {' '}<span className="text-enf-text-primary font-semibold">Combatives</span>,
              {' '}<span className="text-enf-text-primary font-semibold">Recovery</span>, and
              {' '}<span className="text-enf-text-primary font-semibold">Nutrition</span> — built to work together,
              refined by your data, and tuned to whatever you're working through.
            </p>
            <p>
              Enferno is designed to serve as the
              {' '}<span className="text-enf-text-primary">digital backbone of the IronLimb Foundation</span> — connecting
              its community, tracking athletic impact, and translating every rep into measurable real-world change.
            </p>
            <p>
              Every workout logged. Every mile tracked. Every subscription tied to something bigger than the
              individual — <span className="text-enf-accent-fg font-semibold">Phantom Limb Syndrome research</span>,
              {' '}<span className="text-enf-accent-fg font-semibold">non-opioid pain treatment alternatives research</span>,
              and <span className="text-enf-accent-fg font-semibold">support for amputee veterans</span> who deserve better.
            </p>
          </div>
          <div className="mt-10 pt-6 border-t border-enf-surface-border text-xs text-enf-text-muted flex flex-wrap items-center gap-x-5 gap-y-2">
            <span><span className="text-enf-text-muted uppercase tracking-widest mr-1">Built by</span><span className="text-enf-text-primary font-semibold">IronFire Technologies, LLC</span></span>
            <span><span className="text-enf-text-muted uppercase tracking-widest mr-1">Subsidiary of</span><span className="text-enf-text-primary font-semibold">Ironbridge Ventures, LLC</span></span>
            <span><span className="text-enf-text-muted uppercase tracking-widest mr-1">In partnership with</span><span className="text-enf-text-primary font-semibold">IronLimb Foundation</span></span>
          </div>
          <div className="mt-10">
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-accent-contrast rounded-md font-semibold text-sm transition-colors">
              <Sparkles className="w-4 h-4" /> Back to Enferno
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Story;
